/**
 * PURPOSE: Administrar y visualizar la llave eliminatoria del evento 17.
 * RESPONSIBILITIES: Sortear, crear, actualizar clasificados y renderizar una llave conectada.
 * COLLABORATORS: event17Bracket service, BracketMatchCard y BracketConnectorLayer.
 * ROUTE: frontend/src/components/FullScreenModal/FullScreenEliminatoria/Bracket/Bracket2.jsx
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-hot-toast";
import {
  FaArrowsRotate,
  FaCircleCheck,
  FaDiagramProject,
  FaWandMagicSparkles,
  FaXmark,
} from "react-icons/fa6";
import {
  advanceEvent17Bracket,
  generateEvent17Bracket,
  getEvent17Bracket,
  previewEvent17Bracket,
} from "../../../../services/event17Bracket.js";
import BracketConnectorLayer from "./BracketConnectorLayer";
import BracketMatchCard from "./BracketMatchCard";

function getApiMessage(error, fallback) {
  return error?.response?.data?.msg || error?.message || fallback;
}

const buttonBase =
  "inline-flex h-9 items-center justify-center gap-2 rounded-lg px-3 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50";

function Spinner() {
  return (
    <svg
      className="h-4 w-4 animate-spin"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v4l4-4-4-4v4a12 12 0 00-12 12h4z"
      />
    </svg>
  );
}

function LoadingBracket() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="animate-pulse space-y-4">
        <div className="h-5 w-48 rounded bg-slate-200" />
        <div className="h-72 rounded-xl bg-slate-100" />
      </div>
    </div>
  );
}

export default function Bracket2({ idevent, idsport, moodName }) {
  const bracketCanvasRef = useRef(null);
  const [bracket, setBracket] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingGenerate, setLoadingGenerate] = useState(false);
  const [loadingAdvance, setLoadingAdvance] = useState(false);
  const [loadingRefresh, setLoadingRefresh] = useState(false);
  const [error, setError] = useState("");

  const loadBracket = useCallback(
    async ({ silent = false } = {}) => {
      if (silent) setLoadingRefresh(true);
      else setLoadingInitial(true);

      try {
        const data = await getEvent17Bracket({ idevent, idsport });
        setBracket(data);
        setError("");
      } catch (requestError) {
        console.error(requestError);
        const message = getApiMessage(
          requestError,
          "No se pudo cargar la llave"
        );
        setError(message);
        if (silent) toast.error(message);
      } finally {
        setLoadingInitial(false);
        setLoadingRefresh(false);
      }
    },
    [idevent, idsport]
  );

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      setLoadingInitial(true);
      try {
        const data = await getEvent17Bracket({ idevent, idsport });
        if (!cancelled) {
          setBracket(data);
          setError("");
        }
      } catch (requestError) {
        console.error(requestError);
        if (!cancelled) {
          setError(getApiMessage(requestError, "No se pudo cargar la llave"));
        }
      } finally {
        if (!cancelled) setLoadingInitial(false);
      }
    };

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [idevent, idsport]);

  const visibleData = preview || bracket;
  const rounds = Array.isArray(visibleData?.rounds) ? visibleData.rounds : [];
  const pendingBestLosers = useMemo(
    () =>
      (visibleData?.pendingSlots || []).filter(
        (slot) => slot.sourceType === "BEST_LOSER"
      ),
    [visibleData]
  );

  const busy =
    loadingPreview || loadingGenerate || loadingAdvance || loadingRefresh;

  const handlePreview = async () => {
    setLoadingPreview(true);
    try {
      const data = await previewEvent17Bracket({ idevent, idsport });
      setPreview(data);
      setError("");
      toast.success("Sorteo generado");
    } catch (requestError) {
      console.error(requestError);
      toast.error(getApiMessage(requestError, "No se pudo realizar el sorteo"));
    } finally {
      setLoadingPreview(false);
    }
  };

  const confirmReplacement = () => {
    if (!bracket?.hasAnyPhase2Matches) return true;

    if (!bracket?.canReplace) {
      toast.error(
        bracket?.replacementBlockedReason ||
        "La llave existente tiene actividad y no puede reemplazarse"
      );
      return false;
    }

    return window.confirm(
      "Ya existe una llave sin actividad. ¿Deseas reemplazarla por este sorteo?"
    );
  };

  const persistPreview = async (replaceExisting) => {
    const response = await generateEvent17Bracket({
      idevent,
      idsport,
      seedOrder: preview.seedOrder,
      replaceExisting,
    });

    setBracket(response.bracket);
    setPreview(null);
    toast.success("Llave creada");
  };

  const handleGenerate = async () => {
    if (!preview?.seedOrder?.length) return;
    if (!confirmReplacement()) return;

    setLoadingGenerate(true);
    try {
      await persistPreview(Boolean(bracket?.hasAnyPhase2Matches));
    } catch (requestError) {
      console.error(requestError);
      const errorCode = requestError?.response?.data?.code;
      const canReplace = requestError?.response?.data?.details?.canReplace;

      if (errorCode === "BRACKET_REPLACE_CONFIRMATION_REQUIRED" && canReplace) {
        const confirmed = window.confirm(
          "La llave existente puede reemplazarse. ¿Deseas continuar?"
        );
        if (confirmed) await persistPreview(true);
      } else {
        toast.error(getApiMessage(requestError, "No se pudo crear la llave"));
      }
    } finally {
      setLoadingGenerate(false);
    }
  };

  const handleAdvance = async () => {
    setLoadingAdvance(true);
    try {
      const response = await advanceEvent17Bracket({ idevent, idsport });
      setBracket(response.bracket);
      setPreview(null);

      if (response.unresolvedOfficialMatches?.length) {
        toast("Hay partidos oficializados sin un ganador definido", {
          icon: "⚠️",
        });
      } else if (response.updatedStages?.length) {
        toast.success("Clasificados actualizados");
      } else {
        toast("No hay nuevos clasificados", { icon: "ℹ️" });
      }
    } catch (requestError) {
      console.error(requestError);
      toast.error(
        getApiMessage(requestError, "No se pudieron actualizar los clasificados")
      );
    } finally {
      setLoadingAdvance(false);
    }
  };

  if (loadingInitial) return <LoadingBracket />;

  if (error && !bracket) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center">
        <p className="font-semibold text-red-700">{error}</p>
        <button
          type="button"
          onClick={() => loadBracket()}
          className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
        >
          Reintentar
        </button>
      </div>
    );
  }

  const firstRoundCount = rounds[0]?.matches?.length || 1;
  const canvasHeight = Math.max(500, firstRoundCount * 122 + 90);
  const canvasWidth = Math.max(300, rounds.length * 236 + (rounds.length - 1) * 76);

  return (
    <section className="space-y-4 py-4">
      <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-xl font-bold text-slate-900">
            Llave de eliminación
          </h3>
          {preview && (
            <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800">
              Sin guardar
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {!preview && !bracket?.hasBracket && (
            <button
              type="button"
              onClick={handlePreview}
              disabled={busy}
              className={`${buttonBase} border border-slate-300 bg-white text-slate-700 hover:border-blue-300 hover:text-blue-700 focus:ring-blue-200`}
            >
              {loadingPreview ? <Spinner /> : <FaWandMagicSparkles />}
              {loadingPreview ? "Sorteando..." : "Sortear"}
            </button>
          )}

          {preview && (
            <>
              <button
                type="button"
                onClick={handlePreview}
                disabled={busy}
                className={`${buttonBase} border border-slate-300 bg-white text-slate-700 hover:border-blue-300 hover:text-blue-700 focus:ring-blue-200`}
              >
                {loadingPreview ? <Spinner /> : <FaWandMagicSparkles />}
                {loadingPreview ? "Sorteando..." : "Sortear nuevamente"}
              </button>
              <button
                type="button"
                onClick={handleGenerate}
                disabled={busy}
                className={`${buttonBase} bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-200`}
              >
                {loadingGenerate ? <Spinner /> : <FaDiagramProject />}
                {loadingGenerate ? "Creando..." : "Crear llave"}
              </button>
              <button
                type="button"
                onClick={() => setPreview(null)}
                disabled={busy}
                title="Cancelar previsualización"
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-50 hover:text-slate-800 disabled:opacity-50"
              >
                <FaXmark />
              </button>
            </>
          )}

          {!preview && bracket?.hasBracket && (
            <>
              <button
                type="button"
                onClick={handlePreview}
                disabled={busy}
                className={`${buttonBase} border border-slate-300 bg-white text-slate-700 hover:border-blue-300 hover:text-blue-700 focus:ring-blue-200`}
              >
                {loadingPreview ? <Spinner /> : <FaWandMagicSparkles />}
                Nuevo sorteo
              </button>
              <button
                type="button"
                onClick={handleAdvance}
                disabled={busy || bracket?.needsLayoutUpdate}
                title={
                  bracket?.needsLayoutUpdate
                    ? "Primero crea una nueva llave con la distribución actual"
                    : "Actualizar clasificados"
                }
                className={`${buttonBase} bg-emerald-600 text-white hover:bg-emerald-700 focus:ring-emerald-200`}
              >
                {loadingAdvance ? <Spinner /> : <FaCircleCheck />}
                {loadingAdvance ? "Actualizando..." : "Actualizar"}
              </button>
              <button
                type="button"
                onClick={() => loadBracket({ silent: true })}
                disabled={busy}
                title="Actualizar vista"
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-50 hover:text-slate-800 disabled:opacity-50"
              >
                {loadingRefresh ? <Spinner /> : <FaArrowsRotate />}
              </button>
            </>
          )}
        </div>
      </div>

      {bracket?.needsLayoutUpdate && !preview && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900">
          Presiona Actualizar para aplicar la nueva distribución de la llave.
        </div>
      )}

      {bracket?.hasLegacyMatches && !preview && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Hay partidos de eliminación creados fuera de este módulo.
        </div>
      )}

      {rounds.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center">
          <FaDiagramProject className="mx-auto text-3xl text-slate-300" />
          <p className="mt-3 text-sm font-semibold text-slate-600">
            Sortear participantes para generar la llave.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-slate-50 p-4 shadow-inner">
          <div
            ref={bracketCanvasRef}
            className="relative grid"
            style={{
              width: canvasWidth,
              height: canvasHeight,
              gridTemplateColumns: `repeat(${rounds.length}, 236px)`,
              columnGap: 76,
            }}
          >
            <BracketConnectorLayer
              containerRef={bracketCanvasRef}
              rounds={rounds}
            />

            {rounds.map((round, roundIndex) => (
              <div
                key={round.key || round.number}
                className="relative z-10 flex h-full flex-col"
              >
                <div
                  className={`mb-2 rounded-lg px-3 py-2 text-center text-xs font-bold uppercase tracking-wide ${roundIndex === 0
                      ? "bg-blue-600 text-white"
                      : "bg-slate-200 text-slate-700"
                    }`}
                >
                  {round.label}
                </div>

                <div className="flex min-h-0 flex-1 flex-col justify-around py-3">
                  {(round.matches || []).map((match) => (
                    <BracketMatchCard
                      key={match.code}
                      match={match}
                      moodName={moodName}
                      emphasized={roundIndex === 0}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {pendingBestLosers.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-sm font-semibold text-slate-700">
              Cupos manuales pendientes: {pendingBestLosers.length}
            </span>
            <div className="flex flex-wrap gap-2">
              {pendingBestLosers.map((slot) => (
                <span
                  key={`${slot.matchCode}-${slot.side}`}
                  className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800 ring-1 ring-amber-200"
                >
                  {slot.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
